<template>
    <b-modal
        :id="modalId"
        :ref="modalId"
        title-tag="h2"
        :title="creditName"
        :lazy="true"
        :okTitle="okTitle"
        @shown="focusInput"
        footer-class="d-flex justify-content-between"
        no-close-on-backdrop
        no-close-on-esc
    >
        <div v-if="warningMessagePayment">
            <div class="alert-wrapper">
                <div class="alert alert-warning alert-dismissible" role="alert">
                    {{ warningMessagePayment }}
                </div>
            </div>
        </div>

        <div v-if="credit">
            <b-alert show variant="warning" v-if="credit.payment_id">
                <i class="cus-error"></i>
                <span id="dialog-alert-message">
                    Editing this record will affect the related payment record
                </span>
            </b-alert>
        </div>

        <form name="edit-form" v-if="!newCredit && credit">
            <b-form-group
                label-for="date"
                label="Date:"
                label-cols="4"
                class="d-flex align-items-center"
            >
                <span v-if="noUpdate">{{ selectedDate }}</span>
                <date-picker
                    v-else
                    :config="datePickerOptions"
                    v-model="selectedDate"
                    v-validate="'required'"
                    :class="{ 'border-danger': errors.has('date') }"
                    class="form-control"
                    placeholder="Date (required)"
                    name="date"
                    id="date"
                ></date-picker>
            </b-form-group>

            <b-form-group
                label-for="client"
                label="Client:"
                label-cols="4"
                class="d-flex align-items-center"
            >
                <span v-if="noUpdate || credit.balance != credit.amount">{{
                    selectedClient.label
                }}</span>
                <vet-select
                    v-else
                    id="client"
                    ref="client"
                    v-model="selectedClient"
                    :show-menu="false"
                    name="client"
                    module="client"
                    v-validate="'required'"
                    placeholder="Client name (required)"
                    :class="{ 'border-danger': errors.has('client') }"
                />
            </b-form-group>
            <b-form-group
                label-for="reason"
                label="Reason:"
                label-cols="4"
                class="d-flex align-items-center"
                v-if="credit.type != CBILLINGS.CREDIT_PEER.TYPE_DEPOSIT"
            >
                <span v-if="noUpdate">{{ reasonText }}</span>
                <select
                    v-else
                    v-model="input.type"
                    v-validate="'required'"
                    :class="{ 'border-danger': errors.has('reason') }"
                    id="reason"
                    name="reason"
                    class="form-control"
                    aria-invalid="false"
                    :disabled="noUpdate"
                >
                    <option
                        v-for="item in reasonOptions"
                        :value="item.id"
                        :key="item.id"
                        >{{ item.value }}</option
                    >
                </select>
            </b-form-group>
            <b-form-group
                label-for="credit-amount"
                label="Amount:"
                label-cols="4"
                class="d-flex align-items-center"
            >
                <span v-if="noUpdate || credit.balance != credit.amount">
                    {{ _toCurrency(credit.amount, 2) }}
                </span>
                <b-form-input
                    v-else
                    id="credit-amount"
                    v-model="input.amount"
                    :class="{ 'border-danger': errors.has('credit-amount') }"
                    v-validate="'required|min_value:0.01|decimal:2'"
                    name="credit-amount"
                ></b-form-input>
            </b-form-group>
            <b-form-group label-for="note" label="Notes:" label-cols="4">
                <b-form-textarea
                    v-model="input.note"
                    placeholder="Notes (optional)"
                    rows="3"
                    name="note"
                ></b-form-textarea>
            </b-form-group>
        </form>
        <form name="create-form" v-else>
            <b-form-group
                label-for="date"
                label="Date:"
                label-cols="4"
                class="d-flex align-items-center"
            >
                <date-picker
                    :config="datePickerOptions"
                    v-model="selectedDate"
                    v-validate="'required'"
                    :class="{ 'border-danger': errors.has('date') }"
                    class="form-control"
                    placeholder="Date (required)"
                    name="date"
                    id="date"
                ></date-picker>
            </b-form-group>

            <b-form-group
                label-for="client"
                label="Client:"
                label-cols="4"
                class="d-flex align-items-center"
            >
                <span v-if="invoicePage">{{
                    selectedClient ? selectedClient.label : null
                }}</span>
                <vet-select
                    v-else
                    id="client"
                    ref="client"
                    v-model="selectedClient"
                    :show-menu="false"
                    name="client"
                    module="client"
                    v-validate="'required'"
                    placeholder="Client name (required)"
                    :class="{ 'border-danger': errors.has('client') }"
                />
            </b-form-group>
            <b-form-group
                label-for="reason"
                label="Reason:"
                label-cols="4"
                class="d-flex align-items-center"
            >
                <select
                    v-model="input.type"
                    v-validate="'required'"
                    :class="{ 'border-danger': errors.has('reason') }"
                    id="reason"
                    name="reason"
                    class="form-control"
                    aria-invalid="false"
                >
                    <option
                        v-for="item in reasonOptions"
                        :value="item.id"
                        :key="item.id"
                        >{{ item.value }}</option
                    >
                </select>
            </b-form-group>
            <b-form-group
                label-for="credit-amount"
                label="Amount:"
                label-cols="4"
                class="d-flex align-items-center"
            >
                <b-form-input
                    id="credit-amount"
                    v-model="input.amount"
                    :class="{ 'border-danger': errors.has('credit-amount') }"
                    v-validate="'required|min_value:0.01|decimal:2'"
                    name="credit-amount"
                ></b-form-input>
            </b-form-group>
            <b-form-group label-for="note" label="Notes:" label-cols="4">
                <b-form-textarea
                    v-model="input.note"
                    placeholder="Notes (optional)"
                    rows="3"
                    name="note"
                ></b-form-textarea>
            </b-form-group>
        </form>

        <template v-slot:modal-footer>
            <modal-footer
                :isDisabled="
                    isOkDisabled || isLoading || isHasUsersChooseInvoice
                "
                ok-title="Save"
                @ok-action="saveChanges"
                @cancel-action="close"
            />
        </template>
    </b-modal>
</template>
<script type="text/javascript">
import BillingServices from "@/services/billing/billing-credit";
import Reference from "@/services/reference";
import CBILLINGS from "@/constants/billings";

export default {
    components: {
        vetSelect: () => import("@/module/commons/vet-select.vue"),
        modalFooter: () => import("@/components/elements/modal-footer")
    },
    props: {
        modalId: {
            type: String,
            required: true
        },
        clientName: {
            type: [String, Object],
            default: null
        },
        invoicePage: {
            type: String,
            default: null
        },
        okTitle: {
            type: String,
            default: "Save"
        }
    },
    computed: {
        isOkDisabled() {
            // just incase, will need to prevent proceed from click because errors bag didnt capture on first load
            return (
                this.errors.items.length > 0 ||
                this.selectedDate === "--" ||
                !this.selectedClient ||
                this.input.amount < 0.01
            );
        },
        isHasUsersChooseInvoice() {
            return this.warningMessagePayment !== "";
        },
        warningMessagePayment() {
            return this.invoicePage
                ? this.$store.getters["billing/warningMessagePayment"](
                      this.invoicePage
                  )
                : "";
        }
    },
    data() {
        return {
            CBILLINGS: CBILLINGS,
            datePickerOptions: {
                format: "DD/MM/YYYY"
            },
            reasonOptions: [],
            selectedClient: null,
            selectedDate: null,
            creditName: null,
            newCredit: false,
            input: {
                type: 1,
                amount: 0,
                note: null
            },
            noUpdate: false,
            restrictUpdate: false,
            credit: null,
            reasonText: null,
            isLoading: true
        };
    },
    methods: {
        focusInput() {
            if (this.newCredit && !this.invoicePage)
                this.$refs.client.$el.querySelector("input").focus();
        },
        open(credit) {
            this.newCredit = !credit;
            this.isLoading = true;
            if (credit) {
                this.credit = credit;
                this.setRestrictions(credit);
            }
            this.init(credit);
            this.$refs[this.modalId].show();
        },
        init(credit) {
            this.datePickerOptions.format = this.getDateFormat();
            this.creditName = this.invoicePage ? "Add Credit" : "New Credit";
            this.selectedDate = this._now().toFormat(this._dateFormat);
            if (credit) {
                this.input = _.clone(credit);
                this.creditName = "Edit Credit #" + this.input.index;
                this.selectedDate = this._carbonDateDisplay(
                    this.input.credit_date
                );

                // FROM INVENTORY RETURN LIST, THE DATE IS IN STRING NOT CARBON
                if (this.selectedDate === "--") {
                    this.selectedDate = this._setDateDisplay(
                        this.input.credit_date
                    );
                }

                this.selectedClient = {
                    id: this.input.client_id,
                    label: this.clientName || this.input.clientLabel
                };
            } else if (!credit && this.invoicePage) {
                this.selectedClient = {
                    id: this.clientName.id,
                    label: this.clientName.label
                };
            }
            this.input.amount = this.input.amount.toFixed(2);
            this.getReasons();
        },
        setRestrictions(credit) {
            //*************************************************
            //  isEditable definition is more to isConvertable
            //*************************************************
            let base =
                !!credit.return_id ||
                credit.type == CBILLINGS.CREDIT_PEER.TYPE_RETURN ||
                credit.isEditable ||
                credit.type == CBILLINGS.CREDIT_PEER.TYPE_DEPOSIT_OVERPAID ||
                credit.type == CBILLINGS.CREDIT_PEER.TYPE_PAYMENT_OVERPAID ||
                credit.cc_processor;

            this.noUpdate = base;
        },
        getReasons() {
            let params = {
                exclude_return: this.noUpdate ? 0 : 1
            };
            Reference.getCreditType(params)
                .then(response => {
                    this.reasonOptions = response.resources;
                    this.setReasonText(response.resources);
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        setReasonText(options) {
            if (this.input.type) {
                this.reasonText = CBILLINGS.CREDIT_TYPE[this.input.type];
            }
        },
        close() {
            this.isLoading = false;
            this.$refs[this.modalId].hide();
        },
        saveChanges() {
            this.isLoading = true;
            let params = {
                invoice_id: this.invoicePage,
                id: this.input.id,
                client_id: this.selectedClient.id,
                iid: _.get(this.input, "invoices[0].id", 0),
                restrict_update: this.newCredit
                    ? 0
                    : this.noUpdate || this.credit.amount != this.credit.balance
                    ? 1
                    : "",
                start: this._carbonDateDisplay(this.input.credit_date),
                action: "Edit",
                credit_date: this._fromDateStrToDateTime(
                    this.selectedDate,
                    true
                ),
                client: this.selectedClient.label,
                type: this.input.type,
                amount: this.input.amount,
                note: this.input.note
            };

            let services = this.newCredit
                ? BillingServices.createCredit(params, this.input.id)
                : BillingServices.updateCredit(params, this.input.id);

            services
                .then(response => {
                    if (response.code == 200) {
                        this._message(response.message || response.messages);
                        this.$emit("reload");
                    } else {
                        this._message(
                            response.message || response.messages,
                            "danger"
                        );
                        this.$emit("reload");
                    }
                })
                .catch(error => {
                    this._message("Error : Unable to convert credit", "danger");
                    console.error(error);
                })
                .finally(() => {
                    this.close();
                });
        }
    }
};
</script>
